import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export type GovernmentFooterBannerProps = {
  heading: string;
  description: string;
  primaryButtonLabel: string;
  primaryButtonHref?: string;
  secondaryButtonLabel?: string;
  secondaryButtonHref?: string;
  backgroundImages: {
    mobile: string;
    desktop: string;
  };
};

export function GovernmentFooterBanner({
  heading,
  description,
  primaryButtonLabel,
  primaryButtonHref = '/get-started/government',
  secondaryButtonLabel,
  secondaryButtonHref = '#',
  backgroundImages,
}: GovernmentFooterBannerProps) {
  return (
    <Flex
      as='section'
      bg='#030304'
      flexDirection='column'
      justifyContent={{ base: 'flex-end', md: 'center' }}
      minHeight={{ base: '500px', md: '560px' }}
      position='relative'
    >
      <Box display={{ base: 'none', md: 'block' }}>
        <Image
          alt=''
          quality={100}
          sizes='1440px'
          src={backgroundImages.desktop}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          fill
        />
      </Box>

      <Box display={{ base: 'block', md: 'none' }}>
        <Image
          alt=''
          quality={100}
          sizes='1440px'
          src={backgroundImages.mobile}
          style={{
            objectFit: 'contain',
            objectPosition: 'top right',
          }}
          fill
        />
      </Box>

      <Flex alignItems='center' margin='0 auto' maxWidth='content-max-width' position='relative' width='100%'>
        <Flex direction='column' gap={{ base: '16px', md: '48px' }} p='16px' width={{ base: '100%', md: '65%' }}>
          <Flex direction='column' gap={{ base: '12px', md: '24px' }} maxW={{ base: '100%', md: '576px' }}>
            <Heading
              as='h2'
              color='white'
              fontFamily='heading'
              fontSize={{ base: '2.375rem', md: '3.375rem' }}
              fontWeight='600'
              letterSpacing='-0.02em'
              lineHeight='110%'
            >
              {heading}
            </Heading>
            <Text color='grey.100'>{description}</Text>
          </Flex>

          <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }}>
            <ButtonLink
              linkProps={{ href: primaryButtonHref }}
              buttonProps={{
                leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                size: 'icon-left',
                variant: 'solid',
                colorScheme: 'blue',
                w: { base: '100%', md: 'fit-content' },
                onClick: () => {
                  trackEvent({
                    type: 'click',
                    name: 'Government CTA Clicked',
                    label: 'learn_more_clicked',
                    value: 'footer_banner',
                  });
                },
              }}
            >
              {primaryButtonLabel}
            </ButtonLink>
            {secondaryButtonLabel ? (
              <ButtonLink
                linkProps={{ href: secondaryButtonHref }}
                buttonProps={{
                  leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                  size: 'icon-left',
                  variant: 'outline',
                  colorScheme: 'blue',
                  w: { base: '100%', md: 'fit-content' },
                }}
              >
                {secondaryButtonLabel}
              </ButtonLink>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
